import {
  addWrapper,
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
  branch,
} from 'ad-hok'
import { FC } from 'react'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import React from 'react'
import {
  addNavigate,
  addIntPathParam,
  addPathParam,
  addLocation,
} from 'shared/src/utils/addNavigate'
import { typedAs } from 'shared/src/utils/typedAs'
import { addNavigationState } from 'shared/src/utils/addNavigationState'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addResource } from '../utils/addResource'
import { hasErrors, validate } from '../utils/validators'
import { addCmsLoadingAndErrorIndicators } from '../utils/addCmsLoadingAndErrorIndicators'
import { Button } from './Button'
import { ResourceType } from './resource'
import { addFormBoundary, addFormContext } from './formContext'
import { Page } from './Page'
import { addToastContext } from './toastContext'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  containerEmbedded: {
    paddingLeft: 50,
    paddingTop: 5,
    paddingBottom: 5,
    borderLeft: '3px solid rgba(0, 0, 0, 0.28)',
  },
  form: {
    marginBottom: 100,
  },
  formEmbedded: {
    width: '100%',
  },
  error: {
    color: 'red',
  },
  buttons: {
    display: 'flex',
    gap: 20,
    marginBottom: 20,
  },
})

interface Props {
  resource: ResourceType<any, any, any>
  resourceId?: number
  creatingQuery?: string
  embedded?: boolean
  onSave?: (model: any) => void
  onCancel?: () => void
}

export const ResourceEdit: FC<Props> = flowMax(
  addDisplayName('ResourceEdit'),
  addTranslationHelpers,
  branch(
    ({ resourceId }) => !!resourceId,
    flowMax(
      addDataLoadingFromPromise(
        'value',
        ({ resource: { api }, resourceId }) => api.get(resourceId!),
        ['resource', 'resourceId']
      ),
      addCmsLoadingAndErrorIndicators('value'),
      addProps(
        ({
          resource: { singularModelName, inputFromExisting, title },
          value,
          t,
        }) => ({
          initialInput: inputFromExisting(value),
          title: t('admin.resources.general.editTitle', {
            name: singularModelName,
            title: title(value),
          }),
        })
      )
    ),
    addProps(
      ({ resource: { emptyInput, singularModelName }, creatingQuery, t }) => ({
        initialInput: { ...emptyInput(creatingQuery) },
        title: t('admin.resources.general.createTitle', {
          name: singularModelName,
        }),
      })
    )
  ),
  addState('values', 'setValues', ({ initialInput }) => initialInput),
  addState('isSaving', 'setSaving', false),
  addState('formErrors', 'setFormErrors', {}),
  addState('error', 'setError', typedAs<string | null>(null)),
  addNavigate,
  addToastContext,
  addHandlers({
    onSave:
      ({
        resource,
        resourceId,
        values,
        setSaving,
        setFormErrors,
        setError,
        onSave,
        navigate,
        embedded,
        showSuccessToast,
        t,
      }) =>
      () => {
        const formErrors = validate(resource.formValidator, values)
        setFormErrors(formErrors)

        if (hasErrors(formErrors)) {
          return
        }

        const input = resource.formValuesToModelInput(values)

        setSaving(true)

        const promise = resourceId
          ? resource.api.update(resourceId, input)
          : resource.api.create(input)

        promise
          .then((model) => {
            setError(null)
            onSave?.(model)

            if (!resourceId && !embedded) {
              navigate('..')
            }

            showSuccessToast({
              title: t('admin.resources.general.saveSuccessTitle'),
              details: t('admin.resources.general.saveSuccessDetails'),
            })
          })
          .catch((error: Error) => {
            setError(error.message)
          })
          .finally(() => {
            setSaving(false)
          })
      },
  }),
  addProps(({ resource: { saveButtonTitle }, values, t }) => ({
    saveButtonTitle: saveButtonTitle
      ? saveButtonTitle(values)
      : t('admin.resources.general.save'),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addFormBoundary,
  addFormContext,
  ({
    embedded,
    resourceId,
    resource: { Form },
    title,
    values,
    setValues,
    formErrors,
    saveButtonTitle,
    onSave,
    onCancel,
    isSaving,
    formDisabled,
    error,
    t,
    classes,
  }) => (
    <div
      className={cx(classes.container, embedded && classes.containerEmbedded)}
    >
      <div className={classes.buttons}>
        <Button
          text={saveButtonTitle}
          onClick={onSave}
          disabled={isSaving || formDisabled}
        />
        {onCancel && (
          <Button
            text={t('admin.resources.general.cancel')}
            onClick={onCancel}
            type="textWrapped"
            disabled={formDisabled}
          />
        )}
      </div>
      {!embedded && <h1>{title}</h1>}
      <div className={embedded ? classes.formEmbedded : classes.form}>
        <Form
          id={resourceId}
          values={values}
          onChange={setValues}
          disabled={isSaving}
          errors={formErrors}
        />
      </div>
      {error && <p className={classes.error}>{error}</p>}
      <div className={classes.buttons}>
        <Button
          text={saveButtonTitle}
          onClick={onSave}
          disabled={isSaving || formDisabled}
        />
        {onCancel && (
          <Button
            text={t('admin.resources.general.cancel')}
            onClick={onCancel}
            type="textWrapped"
            disabled={formDisabled}
          />
        )}
      </div>
    </div>
  )
)

export const ResourceEditPage: FC = flowMax(
  addDisplayName('ResourceEditPage'),
  addResource,
  addPathParam('resourceName'),
  addIntPathParam('resourceId'),
  addWrapper((render, { resourceName }) => (
    <React.Fragment key={resourceName}>{render()}</React.Fragment>
  )),
  addState('localStorageIds', '_setLocalStorageIds', () =>
    typedAs<number[]>(
      JSON.parse(localStorage.getItem('ids') ?? '[]') as number[]
    )
  ),
  addNavigationState('ids', '_setIds', typedAs<number[]>([])),
  addNavigate,
  addLocation,
  addProps(({ ids, localStorageIds, resourceId, navigate }) => {
    const useIds = ids.length > 0 ? ids : localStorageIds
    const index = useIds.findIndex((id) => id === resourceId)
    const navigationOptions = {
      state: {
        ids: useIds,
      },
    }

    return {
      onNext:
        index !== -1 && index < useIds.length - 1
          ? () => navigate(`../${useIds[index + 1]}`, navigationOptions)
          : undefined,
      onPrev:
        index > 0
          ? () => navigate(`../${useIds[index - 1]}`, navigationOptions)
          : undefined,
    }
  }),
  addHandlers({
    onCancel:
      ({ navigate }) =>
      () => {
        navigate('..')
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ resource, resourceId, onCancel, onPrev, onNext, classes }) => (
    <Page key={resourceId}>
      <div className={classes.buttons}>
        {onPrev && <Button text="prev" onClick={onPrev} />}
        {onNext && <Button text="next" onClick={onNext} />}
      </div>
      <ResourceEdit
        resource={resource}
        resourceId={resourceId}
        onCancel={onCancel}
      />
    </Page>
  )
)
